// Design Doc (DDD) builder — projects a Solution Studio Design Model into the SAME Excel workbook the
// AWS Connect Export tool produces (same downloaded template, same sheet names + columns), so the
// engineer hands the customer a familiar, importable-shaped deliverable for every project.
//
// Rather than reconstruct the live-export's native AWS object shapes (queue/Lex API objects) just to
// feed dataMappers.js, we load the same template via ExcelJS and write Design-Model-derived rows
// straight into the right sheets/columns using the shared constants. Each design-editable sheet also
// gets customer-facing Status + Notes columns and a hidden _id column, so the SAME file round-trips
// back into the Design Model (see lambda/solnstudio/excel + the design-doc/import endpoint).
//
// Lex tabs are populated only when the design actually warrants a bot (decideLexStrategy): a DTMF-only
// solution leaves them blank; a large NLU bot also gets the standalone NLU worksheet (nluWorksheet.js).

import ExcelJS from 'exceljs';
import { downloadTemplate } from '../../excel/templateDownloader';
import { SHEET_NAMES, COLUMN_MAPPINGS, DATA_START_ROW, getColumnLetter } from '../../excel/constants';
import { decideLexStrategy } from './lexStrategy';

const LOCALE_BY_LANG = {
  en: 'en_US', es: 'es_US', fr: 'fr_CA', de: 'de_DE', it: 'it_IT',
  pt: 'pt_BR', ja: 'ja_JP', ko: 'ko_KR', zh: 'zh_CN',
};

function localeId(lang) {
  if (!lang) return 'en_US';
  if (lang.includes('_')) return lang;
  return LOCALE_BY_LANG[lang.toLowerCase()] || lang;
}

function colIndex(letter) {
  let n = 0;
  for (const ch of letter) n = n * 26 + (ch.charCodeAt(0) - 64);
  return n;
}

/** The hidden _id column, placed in the first free column after a sheet's last mapped column. The
 *  Status/Notes review columns were removed (engineers found them clutter); the round-trip now infers
 *  removals from physically deleted rows (see dddRoundTrip.js), so only the _id key column remains. */
function reviewCols(mapping) {
  const last = Math.max(...Object.values(mapping).map(colIndex));
  return { id: getColumnLetter(last + 1) };
}

function utterancesForLang(intent, lang) {
  const u = intent.utterances || {};
  const arr = u[lang] || u[lang?.split('_')[0]] || Object.values(u)[0] || [];
  return Array.isArray(arr) ? arr : [];
}

// Stage 2.6 (C.9): derive Lex-tab values from the Design Model's slot elicitation config instead of
// hardcoding placeholders. elicitationMode is the richer contract; the legacy boolean `required` is
// the fallback. "optional" is the only non-required mode.
function slotIsRequired(slot) {
  if (slot.elicitationMode) return slot.elicitationMode !== 'optional';
  return !!slot.required;
}
function elicitationSetting(slot) {
  return slotIsRequired(slot) ? 'Required' : 'Optional';
}
// An intent's confirmation setting is Enabled when any of its slots asks for a read-back (Stage 2.1
// confirmationReadback), or the intent itself flags a confirmation prompt.
function confirmationSetting(intent) {
  const anyReadback = (intent.slots || []).some((s) => s.confirmationReadback);
  return anyReadback || intent.confirmationPrompt ? 'Enabled' : '';
}

// Resolve the bots to document + the intents each owns. Synthesizes a single bot when the design needs
// Lex but the model hasn't named a bot yet (so the tabs still reflect the intent set).
function resolveBots(model) {
  const intents = model.intents || [];
  const bots = (model.lexBots || []).length
    ? model.lexBots
    : [{ name: `${model.meta?.customer || 'Customer'} Bot`, synthesized: true }];
  return bots.map((bot) => {
    const refs = Array.isArray(bot.intentRefs) && bot.intentRefs.length ? new Set(bot.intentRefs) : null;
    const botIntents = refs ? intents.filter((i) => refs.has(i.name) || refs.has(i.id)) : intents;
    return { bot, intents: botIntents };
  });
}

// ---- Row projections (each returns array of { [colLetter]: value, _id }). ----

function queueRows(model) {
  const C = COLUMN_MAPPINGS.QUEUES;
  return (model.queues || []).map((q) => ({
    [C.NAME]: q.connectName || q.designName || '',
    [C.DESCRIPTION]: q.description || q.notes || '',
    [C.HOURS_OF_OPERATION]: q.hoursOfOperation || '',
    _id: q.id,
  }));
}

// ---- Connect operational resources (Import-tool deployed; produced for every project). ----

const DAY_FULL = {
  MONDAY: 'Monday', TUESDAY: 'Tuesday', WEDNESDAY: 'Wednesday', THURSDAY: 'Thursday',
  FRIDAY: 'Friday', SATURDAY: 'Saturday', SUNDAY: 'Sunday',
};

// Render a model schedule (array of { day, open, close, closed }) into a single cell in the AWS
// Export format the import tool parses (reverseDataMappers.parseHoursOfOperationConfig): comma-
// separated, full day name + 24h "HH:MM-HH:MM" (or "Closed"), e.g. "Monday 09:00-17:00, Saturday Closed".
function formatSchedule(schedule) {
  if (!Array.isArray(schedule) || !schedule.length) return '';
  return schedule
    .map((s) => {
      const day = DAY_FULL[String(s.day || '').toUpperCase()] || s.day || '';
      if (!day) return '';
      if (s.closed || (!s.open && !s.close)) return `${day} Closed`;
      return `${day} ${s.open || ''}-${s.close || ''}`;
    })
    .filter(Boolean)
    .join(', ');
}

function hoursRows(model) {
  const C = COLUMN_MAPPINGS.HOURS_OF_OPERATION;
  return (model.hoursOfOperation || []).map((h) => ({
    [C.NAME]: h.name || '',
    [C.DESCRIPTION]: h.description || '',
    [C.HOURS_OF_OPERATION]: formatSchedule(h.schedule) || h.hours || '',
    [C.TIME_ZONE]: h.timeZone || '',
    _id: h.id,
  }));
}

function agentStatusRows(model) {
  const C = COLUMN_MAPPINGS.AGENT_STATUS;
  return (model.agentStatuses || []).map((a) => ({
    [C.NAME]: a.name || '',
    [C.DESCRIPTION]: a.description || (a.type ? `${a.type} status` : ''),
    _id: a.id,
  }));
}

function routingProfileRows(model) {
  const C = COLUMN_MAPPINGS.ROUTING_PROFILES;
  return (model.routingProfiles || []).map((rp) => ({
    [C.NAME]: rp.name || '',
    [C.DESCRIPTION]: rp.description || '',
    [C.CHANNELS]: (rp.channels || ['VOICE']).join(', '),
    [C.CHAT_CONCURRENCY]: rp.chatConcurrency != null ? rp.chatConcurrency : '',
    [C.TASK_CONCURRENCY]: rp.taskConcurrency != null ? rp.taskConcurrency : '',
    [C.EMAIL_CONCURRENCY]: rp.emailConcurrency != null ? rp.emailConcurrency : '',
    [C.DEFAULT_OUTBOUND_QUEUE]: rp.defaultOutboundQueue || (rp.queues || [])[0] || '',
    [C.ASSOCIATED_QUEUES]: (rp.queues || []).join(', '),
    _id: rp.id,
  }));
}

function quickConnectRows(model) {
  const C = COLUMN_MAPPINGS.QUICK_CONNECTS;
  return (model.quickConnects || []).map((qc) => ({
    [C.NAME]: qc.name || '',
    [C.DESCRIPTION]: qc.description || '',
    [C.TYPE]: qc.type || '',
    [C.DESTINATION]: qc.destination || '',
    [C.CONTACT_FLOW_NAME]: qc.contactFlow || '',
    _id: qc.id,
  }));
}

// Security Profiles is a permission MATRIX (rows = permissions, columns = profiles), not a flat list,
// and the AWS Import tool has no security-profile mapper — so we don't write profiles as rows (that would
// corrupt the matrix) and they don't round-trip. Instead, each CUSTOM profile is added as a column header
// after the built-in profile columns (ADDITIONAL_PROFILES_START), which matches the sheet's orientation
// and is non-destructive.
function writeSecurityProfiles(workbook, model) {
  const profiles = model.securityProfiles || [];
  if (!profiles.length) return;
  const ws = workbook.getWorksheet(SHEET_NAMES.SECURITY_PROFILES);
  if (!ws) return;
  const headerRow = ws.getRow(DATA_START_ROW.SECURITY_PROFILES - 1); // profile-name header row
  const startCol = colIndex(COLUMN_MAPPINGS.SECURITY_PROFILES.ADDITIONAL_PROFILES_START);
  profiles.forEach((sp, i) => {
    headerRow.getCell(startCol + i).value = sp.name || '';
  });
  headerRow.commit();
}

// Agent hierarchy groups: each group's name is placed in the column for its level (1–5). The model is a
// flat list with a level + parent; the export sheet lays levels out across columns A–E.
function hierarchyRows(model) {
  const C = COLUMN_MAPPINGS.USER_HIERARCHY_DATA;
  const LEVEL_COL = [C.LEVEL_ONE, C.LEVEL_TWO, C.LEVEL_THREE, C.LEVEL_FOUR, C.LEVEL_FIVE];
  return (model.hierarchyGroups || []).map((g) => {
    const lvl = Math.min(Math.max(Number(g.level) || 1, 1), 5);
    return { [LEVEL_COL[lvl - 1]]: g.name || '', _id: g.id };
  });
}

function contactFlowRows(model) {
  const C = COLUMN_MAPPINGS.CONTACT_FLOWS;
  return (model.flows || []).map((f) => ({
    [C.NAME]: f.name || '',
    [C.STATE]: 'DESIGN',
    [C.STATUS]: f.source === 'instance-export' ? 'EXISTING' : 'PROPOSED',
    [C.TYPE]: f.type === 'flow_module' || f.type === 'module' ? 'Module' : 'Contact Flow',
    [C.IS_DEFAULT]: 'No',
    [C.CONTACT_FLOW_ERRORS]: 0,
    _id: f.id,
  }));
}

function lambdaRows(model) {
  const C = COLUMN_MAPPINGS.CONNECT_LAMBDA;
  return (model.lambdas || []).map((l) => ({
    [C.FUNCTION_NAME]: l.name || '',
    [C.DESCRIPTION]: l.purpose || l.description || '',
    [C.ASSOCIATED_FLOWS]: '',
    _id: l.id,
  }));
}

function lexBotRows(model, resolved) {
  const C = COLUMN_MAPPINGS.LEX_BOTS_V2;
  const languages = (model.meta?.languages || ['en']).map(localeId).join(', ');
  return resolved.map(({ bot, intents }) => ({
    [C.BOT_NAME]: bot.name || '',
    [C.DESCRIPTION]: bot.description || '',
    [C.SUBJECT_TO_COPPA]: 'No',
    [C.IDLE_SESSION_TIMEOUT]: '300 seconds',
    [C.LANGUAGES]: languages,
    [C.ALIAS_VERSION]: 'TestBotAlias:DRAFT',
    [C.ASSOCIATED_TO_CONNECT]: 'Yes',
    [C.INTENT_COUNT]: intents.length,
    [C.SLOT_TYPE_COUNT]: (model.slotTypes || []).length,
    [C.LEX_VERSION]: 'V2',
    [C.CONVERSATION_METRICS]: 'N/A',
    [C.UTTERANCE_METRICS]: 'N/A',
    [C.ASSOCIATED_FLOWS]: '',
    _id: bot.id || `lexbot:${bot.name}`,
  }));
}

function lexIntentRows(model, resolved) {
  const C = COLUMN_MAPPINGS.LEX_INTENTS;
  const lang = (model.meta?.languages || ['en'])[0];
  const loc = localeId(lang);
  const rows = [];
  for (const { bot, intents } of resolved) {
    for (const intent of intents) {
      rows.push({
        [C.BOT_NAME]: bot.name || '',
        [C.LOCALE_ID]: loc,
        [C.INTENT_NAME]: intent.name || '',
        [C.DESCRIPTION]: intent.description || '',
        [C.SAMPLE_UTTERANCES]: utterancesForLang(intent, lang).join('\n'),
        [C.CONFIRMATION_SETTING]: confirmationSetting(intent),
        [C.SLOT_COUNT]: (intent.slots || []).length,
        [C.FULFILLMENT_TYPE]: intent.selfService ? 'Code Hook' : 'Return Intent',
        [C.PARENT_INTENT_SIGNATURE]: '',
        _id: intent.id,
      });
    }
  }
  return rows;
}

function lexSlotRows(model, resolved) {
  const C = COLUMN_MAPPINGS.LEX_SLOTS_AND_TYPES;
  const loc = localeId((model.meta?.languages || ['en'])[0]);
  const slotTypeByName = new Map((model.slotTypes || []).map((st) => [st.name, st]));
  const rows = [];
  for (const { bot, intents } of resolved) {
    for (const intent of intents) {
      for (const slot of intent.slots || []) {
        const stName = slot.slotType || slot.type || '';
        const st = slotTypeByName.get(stName) || {};
        const values = (st.values || []).map((v) => (typeof v === 'string' ? v : v.value || '')).filter(Boolean).join('\n');
        rows.push({
          [C.BOT_NAME]: bot.name || '',
          [C.LOCALE_ID]: loc,
          [C.INTENT_NAME]: intent.name || '',
          [C.SLOT_NAME]: slot.name || '',
          [C.SLOT_DESCRIPTION]: slot.description || '',
          [C.SLOT_TYPE_ID]: stName,
          [C.SLOT_TYPE_NAME]: stName,
          [C.SLOT_TYPE_DESCRIPTION]: st.description || '',
          [C.SLOT_TYPE_VALUES]: values,
          [C.VALUE_SELECTION_STRATEGY]: st.selectionStrategy || 'ORIGINAL_VALUE',
          [C.VALUE_ELICITATION_SETTING]: elicitationSetting(slot),
          [C.REQUIRED]: slotIsRequired(slot) ? 'Yes' : 'No',
          [C.PARENT_SLOT_TYPE_SIGNATURE]: '',
          [C.EXTERNAL_SOURCE_SETTING]: '',
          // Composite id (slot rows have no standalone model element) — stable for round-trip mapping.
          _id: `${intent.id}::${slot.name}`,
        });
      }
    }
  }
  return rows;
}

// Write rows into a sheet at its data-start row, plus Status/Notes (editable) + hidden _id columns,
// so the workbook round-trips back into the Design Model by id.
function writeSheet(workbook, sheetKey, mapping, rows, { review = true, sheetName, startKey } = {}) {
  // sheetName / startKey overrides exist because a few sheets key their tab name and their
  // column/start-row config differently (e.g. USER_HIERARCHIES tab ↔ USER_HIERARCHY_DATA columns).
  const ws = workbook.getWorksheet(sheetName || SHEET_NAMES[sheetKey]);
  if (!ws) return;
  const start = DATA_START_ROW[startKey || sheetKey];
  const rc = review ? reviewCols(mapping) : null;
  if (rc) {
    const header = ws.getRow(start - 1);
    header.getCell(rc.id).value = '_id';
    header.commit();
    ws.getColumn(rc.id).hidden = true;
  }
  rows.forEach((row, i) => {
    const r = ws.getRow(start + i);
    for (const [col, value] of Object.entries(row)) {
      if (col === '_id') continue;
      r.getCell(col).value = value;
    }
    if (rc) {
      r.getCell(rc.id).value = row._id || '';
    }
    r.commit();
  });
}

function safeName(model, projectName) {
  return (projectName || model.meta?.customer || 'Project').replace(/[^a-z0-9]+/gi, '_').replace(/^_+|_+$/g, '');
}

function triggerDownload(buffer, filename) {
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/**
 * Build the DDD workbook (AWS Export format) for a Design Model into an ArrayBuffer (no download).
 * Used by the deliverables orchestrator to persist the file as a versioned artifact.
 * @param {object} designModel
 * @returns {Promise<{ buffer: ArrayBuffer, strategy: string }>}
 */
export async function buildDddArrayBuffer(designModel) {
  const model = designModel || {};
  const strategyInfo = decideLexStrategy(model);

  const templateData = await downloadTemplate();
  const workbook = new ExcelJS.Workbook();
  await workbook.xlsx.load(templateData);

  // Core Connect config — produced for every project.
  writeSheet(workbook, 'HOURS_OF_OPERATION', COLUMN_MAPPINGS.HOURS_OF_OPERATION, hoursRows(model));
  writeSheet(workbook, 'QUEUES', COLUMN_MAPPINGS.QUEUES, queueRows(model));
  writeSheet(workbook, 'ROUTING_PROFILES', COLUMN_MAPPINGS.ROUTING_PROFILES, routingProfileRows(model));
  writeSheet(workbook, 'AGENT_STATUS', COLUMN_MAPPINGS.AGENT_STATUS, agentStatusRows(model));
  writeSheet(workbook, 'QUICK_CONNECTS', COLUMN_MAPPINGS.QUICK_CONNECTS, quickConnectRows(model));
  writeSecurityProfiles(workbook, model);
  writeSheet(workbook, 'USER_HIERARCHY_DATA', COLUMN_MAPPINGS.USER_HIERARCHY_DATA, hierarchyRows(model),
    { sheetName: SHEET_NAMES.USER_HIERARCHIES });
  writeSheet(workbook, 'CONTACT_FLOWS', COLUMN_MAPPINGS.CONTACT_FLOWS, contactFlowRows(model));
  writeSheet(workbook, 'CONNECT_LAMBDA', COLUMN_MAPPINGS.CONNECT_LAMBDA, lambdaRows(model));

  // Lex tabs — only when the design warrants a bot (simple bot lives here; large NLU also gets the
  // standalone worksheet). DTMF-only leaves these blank.
  if (strategyInfo.needsLex) {
    const resolved = resolveBots(model);
    writeSheet(workbook, 'LEX_BOTS_V2', COLUMN_MAPPINGS.LEX_BOTS_V2, lexBotRows(model, resolved));
    writeSheet(workbook, 'LEX_INTENTS', COLUMN_MAPPINGS.LEX_INTENTS, lexIntentRows(model, resolved));
    writeSheet(workbook, 'LEX_SLOTS_AND_TYPES', COLUMN_MAPPINGS.LEX_SLOTS_AND_TYPES, lexSlotRows(model, resolved));
  }

  const buffer = await workbook.xlsx.writeBuffer();
  return { buffer, strategy: strategyInfo.strategy };
}

/** Build + trigger a direct browser download of the DDD workbook. */
export async function buildDddWorksheet(designModel, { projectName, filename } = {}) {
  const { buffer, strategy } = await buildDddArrayBuffer(designModel);
  triggerDownload(buffer, filename || `${safeName(designModel || {}, projectName)}_DDD.xlsx`);
  return { strategy };
}

export { safeName as dddSafeName };
export default buildDddWorksheet;
